import Highcharts from "highcharts";

// Custom marker symbol for the prior-period tick: a thin fixed-width vertical
// bar drawn inside the point's (radius-based) bounding box. Registered once —
// this makes the tick a real Highcharts point (hoverable, own tooltip),
// instead of a plain SVG shape with no interaction.
if (!(Highcharts.SVGRenderer.prototype.symbols as any).priorTick) {
  (Highcharts.SVGRenderer.prototype.symbols as any).priorTick = (
    x: number,
    y: number,
    w: number,
    h: number,
  ) => {
    const lineWidth = 2;
    const cx = x + w / 2 - lineWidth / 2;
    return ["M", cx, y, "L", cx + lineWidth, y, "L", cx + lineWidth, y + h, "L", cx, y + h, "Z"];
  };
}

// Prior-period comparison, attached to a sub-stage (or the top-level totals).
// "performance" is the semantic driver for color (improving = good = green,
// worsening = bad = red) — NOT "trend", since for a duration metric a
// literal increase ("up") is bad news. "trend" only drives the arrow glyph.
type Comparison = {
  prior_value?: number;
  change?: number;
  change_percent?: number;
  trend?: string;
  display_value?: string;
  normalized_value?: number;
  cases_participated?: number;
  variance_seconds?: number;
  variance_display?: string;
  variance_hours?: number;
  performance?: string;
};

type SubStage = {
  key: string;
  label: string;
  display_value: string;
  normalized_value: number;
  order?: number;
  cases_participated?: number;
  comparison?: Comparison;
};

type Stage = {
  key: string;
  label: string;
  color: string;
  order?: number;
  cases_participated?: number;
  sub_stages: SubStage[];
};

type GroupedBarInput = {
  normalized_unit?: string;
  y_axis_label?: string;
  avg_total_lifecycle_human?: string;
  total_cases_analyzed?: number;
  stages?: Stage[];
  // Overall prior comparison for avg_total_lifecycle_human.
  comparison?: Comparison;
};

// ── Shared comparison presentation helpers ──────────────────────────────────
// Same semantic palette as KpiComparison's trend chip elsewhere in the app
// (green/red/gray), but keyed off "performance" rather than raw trend so the
// color always means "good/bad", never just "number went up".
const PERF_COLORS: Record<string, { text: string; bg: string }> = {
  improving: { text: "#166534", bg: "rgba(22, 101, 52, 0.12)" },
  worsening: { text: "#b91c1c", bg: "rgba(185, 28, 28, 0.12)" },
};
const NEUTRAL_COLOR = { text: "#475569", bg: "rgba(71, 85, 105, 0.12)" };

const perfColor = (cmp?: Comparison): { text: string; bg: string } =>
  PERF_COLORS[String(cmp?.performance ?? "").toLowerCase()] ?? NEUTRAL_COLOR;

const trendArrow = (cmp?: Comparison): string => {
  const trend = String(cmp?.trend ?? "").toLowerCase();
  if (trend === "up") return "▲";
  if (trend === "down") return "▼";
  return "→";
};

const formatPct = (cmp?: Comparison): string => {
  const pct = cmp?.change_percent;
  if (typeof pct !== "number" || !Number.isFinite(pct)) return "";
  const abs = Math.abs(Math.round(pct * 10) / 10);
  const display = Number.isInteger(abs) ? `${abs}` : abs.toFixed(1).replace(/\.0$/, "");
  return `${display}%`;
};

const sortByOrder = <T extends { order?: number }>(items: T[]): T[] =>
  [...items].sort((a, b) => {
    const left = Number.isFinite(Number(a.order))
      ? Number(a.order)
      : Number.MAX_SAFE_INTEGER;
    const right = Number.isFinite(Number(b.order))
      ? Number(b.order)
      : Number.MAX_SAFE_INTEGER;
    return left - right;
  });

const toRgba = (hex: string, alpha: number): string => {
  const normalized = hex.replace("#", "");
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((c) => `${c}${c}`)
          .join("")
      : normalized;
  if (expanded.length !== 6) return hex;
  const r = parseInt(expanded.slice(0, 2), 16);
  const g = parseInt(expanded.slice(2, 4), 16);
  const b = parseInt(expanded.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${Math.min(Math.max(alpha, 0), 1)})`;
};

const unwrap = (data: unknown): GroupedBarInput => {
  const raw = (data ?? {}) as Record<string, unknown>;
  if (Array.isArray(raw.stages)) return raw as unknown as GroupedBarInput;
  if (
    raw.data &&
    typeof raw.data === "object" &&
    Array.isArray((raw.data as any).stages)
  )
    return raw.data as GroupedBarInput;
  return raw as unknown as GroupedBarInput;
};

type BarPoint = {
  category: string;
  value: number;
  color: string;
  stageLabel: string;
  subLabel: string;
  displayValue: string;
  isSingle: boolean;
  isSubStage: boolean;
  casesParticipated?: number;
  comparison?: Comparison;
};

type StageGroup = {
  label: string;
  color: string;
  startIndex: number;
  endIndex: number;
};

const buildGroupedBarOptions = (data: unknown): Highcharts.Options => {
  const input = unwrap(data);
  const stages = Array.isArray(input.stages) ? sortByOrder(input.stages) : [];
  const unit = input.normalized_unit ?? "hours";
  const lifecycleHuman = input.avg_total_lifecycle_human ?? "";

  const points: BarPoint[] = [];
  const stageGroups: StageGroup[] = [];

  stages.forEach((stage) => {
    const subStages = Array.isArray(stage.sub_stages)
      ? sortByOrder(stage.sub_stages)
      : [];
    if (subStages.length === 0) return;

    const isSingle = subStages.length === 1;
    const startIndex = points.length;

    subStages.forEach((sub, subIdx) => {
      const shade = !isSingle
        ? toRgba(stage.color, Math.max(0.92 - subIdx * 0.18, 0.45))
        : stage.color;

      points.push({
        category: isSingle ? stage.label : sub.label,
        value: sub.normalized_value,
        color: shade,
        stageLabel: stage.label,
        subLabel: sub.label,
        displayValue: sub.display_value,
        isSingle,
        isSubStage: !isSingle,
        casesParticipated: sub.cases_participated ?? stage.cases_participated,
        comparison: sub.comparison,
      });
    });

    stageGroups.push({
      label: stage.label,
      color: stage.color,
      startIndex,
      endIndex: points.length - 1,
    });
  });

  const categories = points.map((p) => p.category);
  const chartHeight = Math.max(320, points.length * 58 + 90);
  const hasAnyComparison = points.some((p) => p.comparison);

  // The prior-period tick can sit past the current bar's own length (a
  // substage can have gotten faster or slower since). Without this, the
  // y-axis auto-scales to the current values only and a prior tick beyond
  // that range would render off the plot.
  const axisMax = hasAnyComparison
    ? Math.max(
        ...points.map((p) => p.value),
        ...points.map((p) => p.comparison?.normalized_value ?? 0),
      ) * 1.08
    : undefined;

  // Left-margin layout. The margin holds two label lanes that must not
  // overlap: the far-left stage (group) lane and the substage category
  // lane next to the bars. Both wrap to their width so long names show in
  // full without truncation.
  const GROUP_LABEL_X = 16;
  const GROUP_LABEL_WIDTH = 96;
  const CATEGORY_LABEL_WIDTH = 140;
  const CHART_LEFT_MARGIN = 275;
  // Extra right-hand room for the per-bar "vs prior" delta chip. Only
  // reserved when at least one point actually carries a comparison, so
  // charts on the old payload shape render exactly as before.
  const CHART_RIGHT_MARGIN = 96;

  // Prior-period tick points — a real scatter series (not a plain drawn
  // shape) so each tick is genuinely hoverable with its own tooltip. Sparse:
  // only points whose sub-stage has a comparison get an entry, with an
  // explicit x (category index) since the array is no longer 1:1 with rows.
  const priorPoints = points.reduce<Array<Record<string, unknown>>>(
    (acc, p, i) => {
      const priorValue = p.comparison?.normalized_value;
      if (typeof priorValue === "number" && Number.isFinite(priorValue)) {
        acc.push({
          x: i,
          y: priorValue,
          custom: {
            stageLabel: p.stageLabel,
            subLabel: p.subLabel,
            isSingle: p.isSingle,
            comparison: p.comparison,
          },
        });
      }
      return acc;
    },
    [],
  );

  const series: Highcharts.SeriesOptionsType[] = [
    {
      type: "bar" as const,
      name: "Duration",
      showInLegend: false,
      colorByPoint: true,
      data: points.map((p) => ({
        y: p.value,
        color: p.color,
        custom: {
          displayValue: p.displayValue,
          subLabel: p.subLabel,
          stageLabel: p.stageLabel,
          isSingle: p.isSingle,
          isSubStage: p.isSubStage,
          casesParticipated: p.casesParticipated,
          // Not read by the bar's own tooltip (hovering a bar shows only its
          // current data) — kept here because the always-visible delta chip
          // dataLabel below reads it from this same point.
          comparison: p.comparison,
        },
      })),
    },
    // Rendered after the bar series so its marker sits on top and wins
    // hover priority over the bar for the small area the tick occupies.
    {
      type: "scatter" as const,
      name: "Prior period avg",
      showInLegend: false,
      data: priorPoints,
      marker: {
        symbol: "priorTick",
        radius: 17,
        fillColor: "#0F172A",
        lineWidth: 0,
        states: { hover: { fillColorOpacity: 1 } },
      },
    } as any,
  ];

  const legendItems = stages.map((s) => ({
    name: s.label,
    color: s.color,
  }));

  return {
    chart: {
      type: "bar",
      animation: false,
      height: chartHeight,
      marginTop: lifecycleHuman ? 40 : 10,
      marginLeft: CHART_LEFT_MARGIN,
      marginRight: hasAnyComparison ? CHART_RIGHT_MARGIN : undefined,
      // Only the legend lives below the plot now (no axis labels/title).
      marginBottom: 50,
      events: {
        render() {
          const chart = this as Highcharts.Chart & {
            _gbGroups?: Highcharts.SVGElement[];
            _gbLegend?: Highcharts.SVGElement[];
            _gbSubtitle?: Highcharts.SVGElement;
            _gbDividers?: Highcharts.SVGElement[];
          };

          if (chart._gbGroups) {
            chart._gbGroups.forEach((el) => el.destroy());
          }
          chart._gbGroups = [];

          if (chart._gbSubtitle) {
            chart._gbSubtitle.destroy();
            chart._gbSubtitle = undefined;
          }
          if (lifecycleHuman) {
            const badgeLabel = lifecycleHuman;
            const overallCmp = input.comparison;
            const overallChip = overallCmp
              ? ` <span style="font-weight:700;font-size:11px;color:${perfColor(overallCmp).text};">${trendArrow(overallCmp)} ${formatPct(overallCmp)} vs Prior</span>`
              : "";
            chart._gbSubtitle = chart.renderer
              .label(
                `<span style="color:#64748B;font-size:10px;">Total Avg Time </span>` +
                  `<span style="font-weight:700;font-size:12px;color:#0F172A;">${badgeLabel}</span>` +
                  overallChip,
                0,
                6,
              )
              .attr({
                zIndex: 7,
                padding: 6,
                r: 6,
                fill: "#F1F5F9",
                stroke: "#E2E8F0",
                "stroke-width": 1,
              })
              .css({ fontSize: "11px" })
              .add();

            const bbox = chart._gbSubtitle.getBBox();
            chart._gbSubtitle.attr({ x: chart.chartWidth - bbox.width - 12 });
          }

          const xAxis = chart.xAxis[0];
          if (!xAxis) return;

          stageGroups.forEach((group) => {
            const topPos = xAxis.toPixels(group.startIndex, true);
            const bottomPos = xAxis.toPixels(group.endIndex, true);
            const midY = chart.plotTop + (topPos + bottomPos) / 2;
            const stripeX = 6;
            const barTop = chart.plotTop + Math.min(topPos, bottomPos) - 8;
            const barHeight = Math.abs(bottomPos - topPos) + 16;

            const stripe = chart.renderer
              .rect(stripeX, barTop, 3, barHeight, 1)
              .attr({ fill: group.color, zIndex: 7 })
              .add();
            chart._gbGroups!.push(stripe);

            // Stage name wraps to its lane width (SVG tspans) so long names
            // show in full — no truncation. Anchor a little above the group
            // midpoint so a 2-line name stays visually centred on its bars.
            const groupLabel = chart.renderer
              .text(group.label, GROUP_LABEL_X, midY)
              .attr({ zIndex: 7 })
              .css({
                fontSize: "10px",
                fontWeight: "700",
                color: group.color,
                width: GROUP_LABEL_WIDTH,
                whiteSpace: "normal",
              } as Highcharts.CSSObject)
              .add();

            // Vertically re-centre now that the wrapped height is known.
            const labelHeight = groupLabel.getBBox().height;
            groupLabel.attr({ y: midY - labelHeight / 2 + 10 });
            chart._gbGroups!.push(groupLabel);
          });

          // Divider between stage (main) groups — a thin full-width rule so
          // e.g. "Medical Benefit"'s rows read as visually distinct from
          // "Pharmacy Benefit"'s, not just via the left-margin stripe/label.
          if (chart._gbDividers) {
            chart._gbDividers.forEach((el) => el.destroy());
          }
          chart._gbDividers = [];

          for (let g = 0; g < stageGroups.length - 1; g++) {
            const cur = stageGroups[g]!;
            const next = stageGroups[g + 1]!;
            const curEndY = chart.plotTop + xAxis.toPixels(cur.endIndex, true);
            const nextStartY =
              chart.plotTop + xAxis.toPixels(next.startIndex, true);
            const boundaryY = (curEndY + nextStartY) / 2;

            const divider = chart.renderer
              .path(["M", 0, boundaryY, "L", chart.chartWidth, boundaryY])
              .attr({ stroke: "#E2E8F0", "stroke-width": 1, zIndex: 6 })
              .add();
            chart._gbDividers!.push(divider);
          }

          if (chart._gbLegend) {
            chart._gbLegend.forEach((el) => el.destroy());
          }
          chart._gbLegend = [];

          const itemW = 150;
          const dotR = 5;
          const gap = 8;
          // "Prior period avg" gets its own, narrower legend slot (a tick
          // glyph instead of a dot) appended after the stage colors.
          const priorItemW = 140;
          const totalW =
            legendItems.length * itemW + (hasAnyComparison ? priorItemW : 0);
          let startX = Math.max(
            chart.plotLeft,
            (chart.chartWidth - totalW) / 2,
          );
          const legendY = chart.chartHeight - 25;

          legendItems.forEach((item) => {
            const dot = chart.renderer
              .circle(startX + dotR, legendY, dotR)
              .attr({ fill: item.color, zIndex: 7 })
              .add();
            chart._gbLegend!.push(dot);

            const text = chart.renderer
              .text(item.name, startX + dotR * 2 + gap, legendY + 4)
              .attr({ zIndex: 7 })
              .css({
                fontSize: "11px",
                fontWeight: "600",
                color: "#334155",
              })
              .add();
            chart._gbLegend!.push(text);

            startX += itemW;
          });

          if (hasAnyComparison) {
            const tickGlyph = chart.renderer
              .rect(startX + dotR - 1, legendY - 7, 2, 14, 0)
              .attr({ fill: "#0F172A", zIndex: 7 })
              .add();
            chart._gbLegend!.push(tickGlyph);

            const tickText = chart.renderer
              .text("Prior period avg", startX + dotR * 2 + gap, legendY + 4)
              .attr({ zIndex: 7 })
              .css({
                fontSize: "11px",
                fontWeight: "600",
                color: "#334155",
              })
              .add();
            chart._gbLegend!.push(tickText);
          }
        },
      },
    },
    title: { text: "" },
    credits: { enabled: false },
    accessibility: { enabled: false },
    xAxis: {
      categories,
      title: { text: null },
      lineWidth: 0,
      tickWidth: 0,
      labels: {
        // SVG text wrapping (NOT useHTML). HTML axis labels on inverted (bar)
        // charts ignore `width` and collapse to one character per line; native
        // SVG labels wrap at word boundaries so long stage names show in full,
        // no truncation. Width bounds the wrap; whiteSpace:normal enables it.
        useHTML: false,
        style: {
          color: "#334155",
          fontSize: "11px",
          fontWeight: "500",
          width: CATEGORY_LABEL_WIDTH,
          whiteSpace: "normal",
        } as Highcharts.CSSObject,
      },
    },
    // No axis chrome: every bar already carries its own value (+ delta chip)
    // as a label, so the tick numbers/unit title/vertical gridlines were
    // pure duplication — and on a shared 0–260-ish hour scale, they read as
    // clutter more than guidance.
    yAxis: {
      min: 0,
      max: axisMax,
      title: { text: null },
      labels: { enabled: false },
      gridLineWidth: 0,
      lineWidth: 0,
      tickWidth: 0,
    },
    tooltip: {
      shared: false,
      useHTML: true,
      outside: true,
      borderWidth: 0,
      borderRadius: 10,
      backgroundColor: "#0F172A",
      shadow: false,
      padding: 0,
      formatter() {
        const ctx = this as any;
        const c = ctx?.point?.options?.custom ?? {};
        const stageLabel = c.stageLabel ?? "";
        const subLabel = c.subLabel ?? "";
        const isSingle = Boolean(c.isSingle);

        // Hovering the prior-period tick: only that period's own data —
        // no current value, no delta.
        if (ctx?.series?.type === "scatter") {
          const cmp = c.comparison as Comparison | undefined;
          if (!cmp) return false;

          const casesRow =
            cmp.cases_participated !== undefined
              ? `<div style="margin-top:6px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.15);display:flex;align-items:center;gap:6px;">
                  <span style="opacity:0.7;font-size:11px;">Cases:</span>
                  <span style="font-weight:700;font-size:12px;color:#7DD3FC;">${cmp.cases_participated.toLocaleString()}</span>
                </div>`
              : "";

          return `
            <div style="padding:10px 12px;min-width:200px;color:#f8fafc;font-size:12px;line-height:1.35;">
              <div style="font-weight:700;font-size:13px;margin-bottom:2px;">${stageLabel}</div>
              ${!isSingle ? `<div style="opacity:0.78;font-size:11px;margin-bottom:6px;">${subLabel}</div>` : ""}
              <div style="opacity:0.65;font-size:11px;margin-bottom:2px;">Prior period</div>
              <div style="font-weight:700;font-size:15px;margin-bottom:2px;">${cmp.display_value ?? "–"}</div>
              <div style="opacity:0.6;font-size:11px;">${cmp.normalized_value ?? ""} ${unit}</div>
              ${casesRow}
            </div>
          `;
        }

        // Hovering the bar: only the current period's own data.
        const displayValue = c.displayValue ?? "";
        const casesParticipated = c.casesParticipated as number | undefined;

        const casesRow =
          casesParticipated !== undefined
            ? `<div style="margin-top:6px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.15);display:flex;align-items:center;gap:6px;">
                <span style="opacity:0.7;font-size:11px;">Cases:</span>
                <span style="font-weight:700;font-size:12px;color:#7DD3FC;">${casesParticipated.toLocaleString()}</span>
              </div>`
            : "";

        return `
          <div style="padding:10px 12px;min-width:200px;color:#f8fafc;font-size:12px;line-height:1.35;">
            <div style="font-weight:700;font-size:13px;margin-bottom:2px;">${stageLabel}</div>
            ${!isSingle ? `<div style="opacity:0.78;font-size:11px;margin-bottom:6px;">${subLabel}</div>` : ""}
            <div style="font-weight:700;font-size:15px;margin-bottom:2px;">${displayValue}</div>
            <div style="opacity:0.6;font-size:11px;">${ctx.y} ${unit}</div>
            ${casesRow}
          </div>
        `;
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 3,
        pointWidth: 30,
        pointPadding: 0.05,
        groupPadding: 0.12,
        borderWidth: 0,
        states: {
          inactive: { opacity: 0.35 },
          hover: { enabled: true, brightness: 0.06 },
        },
        // Two labels per bar: the value inside (unchanged), and — only when
        // the point carries a prior comparison — a small delta chip just
        // past the bar's tip so the change reads at a glance, no hover
        // needed. Highcharts supports an array of dataLabels per series.
        dataLabels: [
          {
            enabled: true,
            inside: true,
            style: {
              color: "#0B1220",
              fontWeight: "700",
              textOutline: "none",
              fontSize: "10px",
            },
            formatter() {
              const c = (this as any).point?.options?.custom ?? {};
              return c.displayValue ?? "";
            },
          },
          {
            enabled: hasAnyComparison,
            inside: false,
            useHTML: true,
            allowOverlap: true,
            crop: false,
            overflow: "allow" as const,
            x: 6,
            style: { fontSize: "10px" },
            formatter() {
              const c = (this as any).point?.options?.custom ?? {};
              const cmp = c.comparison as Comparison | undefined;
              if (!cmp) return null as unknown as string;
              const { text, bg } = perfColor(cmp);
              return `<span style="display:inline-flex;align-items:center;gap:3px;padding:2px 7px;border-radius:999px;font-size:10px;font-weight:700;color:${text};background:${bg};white-space:nowrap;">${trendArrow(cmp)} ${formatPct(cmp)}</span>`;
            },
          },
        ] as any,
      },
    },
    legend: { enabled: false },
    exporting: {
      enabled: true,
      fallbackToExportServer: true,
      buttons: {
        contextButton: {
          align: "right",
          verticalAlign: "top",
          y: -43,
          x: -20,
        },
      },
    },
    series,
  };
};

export function renderGroupedBar(
  data: unknown,
  _spec: unknown,
): Highcharts.Options {
  return buildGroupedBarOptions(data);
}
